/**
 * Product Question Generator
 *
 * Select top products from a category and generate specific questions for them.
 *
 * Date: 2025-11-16
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sparkles,
  Play,
  AlertCircle,
  CheckCircle2,
  Package,
  Search,
  ArrowLeft,
  Globe,
  Info,
} from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';
import { useToast } from '@/components/ui/use-toast';

interface Product {
  id: string;
  product_name: string;
  category: string;
  price?: number;
  currency?: string;
  attribute_count?: number;
}

const QUESTION_TYPES = [
  { id: 'PC-PA-PROD', name: 'Product Attribute Questions', description: '¿Este producto tiene X?', defaultSelected: true },
  { id: 'PC-PB-PROD', name: 'Product Benefit Questions', description: '¿Este producto ofrece X beneficio?', defaultSelected: true },
  { id: 'PC-BP-PROD', name: 'Product Use-Case Questions', description: '¿Este producto es adecuado para X?', defaultSelected: false },
];

export function ProductQuestionGenerator() {
  const { sectorId, siteId, wheelId } = useParams<{ sectorId?: string; siteId?: string; wheelId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  const isSiteMode = !!siteId;
  const contextId = siteId || sectorId;

  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProducts, setSelectedProducts] = useState<Set<string>>(new Set());
  const [selectedQuestionTypes, setSelectedQuestionTypes] = useState<string[]>(
    QUESTION_TYPES.filter(qt => qt.defaultSelected).map(qt => qt.id)
  );
  const [selectedLanguage, setSelectedLanguage] = useState<string>('es');
  const [wheelName, setWheelName] = useState<string>('');
  const [sectorName, setSectorName] = useState<string>('');
  const [siteName, setSiteName] = useState<string>('');
  const [questionsPerProduct, setQuestionsPerProduct] = useState<number>(10);

  useEffect(() => {
    loadData();
  }, [sectorId, siteId, wheelId]);

  useEffect(() => {
    // Filter products based on search term
    if (searchTerm.trim() === '') {
      setFilteredProducts(products);
    } else {
      const filtered = products.filter(product =>
        product.product_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredProducts(filtered);
    }
  }, [searchTerm, products]);

  const loadData = async () => {
    try {
      setLoading(true);

      if (isSiteMode && siteId) {
        // SITE MODE: Load site details and products
        const siteResponse = await authFetch(`/api/sites/${siteId}`);
        const siteData = await siteResponse.json();
        setSiteName(siteData.site?.domain || 'Unknown Site');

        // Load wheel details from site wheels endpoint
        const wheelsResponse = await authFetch(`/api/sites/${siteId}/question-wheels`);
        const wheelsData = await wheelsResponse.json();
        const wheel = wheelsData.wheels?.find((w: any) => w.id === wheelId);
        setWheelName(wheel?.center_term || 'Unknown Category');

        // Load products from site's product feed
        const productsResponse = await authFetch(
          `/api/sites/${siteId}/products?wheelId=${wheelId}&limit=2000`
        );
        const productsData = await productsResponse.json();

        if (!productsData.success) {
          throw new Error(productsData.error || 'Failed to load products');
        }

        setProducts(productsData.products || []);
        setFilteredProducts(productsData.products || []);
      } else if (sectorId) {
        // SECTOR MODE: Load sector details and products
        const sectorResponse = await authFetch(`/api/sectors/${sectorId}`);
        const sectorData = await sectorResponse.json();
        setSectorName(sectorData.sector?.name || 'Unknown Sector');

        // Load wheel details from sector wheels endpoint
        const wheelsResponse = await authFetch(`/api/sectors/${sectorId}/question-wheels`);
        const wheelsData = await wheelsResponse.json();
        const wheel = wheelsData.wheels?.find((w: any) => w.id === wheelId);
        setWheelName(wheel?.name || 'Unknown Category');

        // Load products for this wheel's category
        const productsResponse = await authFetch(
          `/api/sectors/${sectorId}/products?wheelId=${wheelId}&limit=2000`
        );
        const productsData = await productsResponse.json();

        if (!productsData.success) {
          throw new Error(productsData.error || 'Failed to load products');
        }

        setProducts(productsData.products || []);
        setFilteredProducts(productsData.products || []);
      }
    } catch (error: any) {
      console.error('Error loading data:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to load products',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleProductToggle = (productId: string) => {
    setSelectedProducts((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(productId)) {
        newSet.delete(productId);
      } else {
        newSet.add(productId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedProducts.size === filteredProducts.length) {
      setSelectedProducts(new Set());
    } else {
      setSelectedProducts(new Set(filteredProducts.map(p => p.id)));
    }
  };

  const handleQuestionTypeToggle = (questionTypeId: string) => {
    setSelectedQuestionTypes((prev) =>
      prev.includes(questionTypeId)
        ? prev.filter((id) => id !== questionTypeId)
        : [...prev, questionTypeId]
    );
  };

  const handleGenerate = async () => {
    if (selectedProducts.size === 0) {
      toast({
        title: 'Validation Error',
        description: 'Please select at least one product',
        variant: 'destructive',
      });
      return;
    }

    if (selectedQuestionTypes.length === 0) {
      toast({
        title: 'Validation Error',
        description: 'Please select at least one question type',
        variant: 'destructive',
      });
      return;
    }

    try {
      setGenerating(true);

      const endpoint = isSiteMode
        ? `/api/sites/${siteId}/generate-product-questions`
        : `/api/sectors/${sectorId}/generate-product-questions`;

      const response = await authFetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          wheelId,
          productIds: Array.from(selectedProducts),
          language: selectedLanguage,
          questionTypes: selectedQuestionTypes,
          questionsPerProduct,
        }),
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to start generation');
      }

      toast({
        title: 'Generation Started',
        description: data.message,
      });

      // Navigate back to question wheel research
      if (isSiteMode) {
        navigate('/research/question-wheel');
      } else {
        navigate(`/sectors/${sectorId}/question-generation/monitor/${data.batchJobId}`);
      }
    } catch (error: any) {
      console.error('Error starting generation:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to start generation',
        variant: 'destructive',
      });
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(isSiteMode ? '/research/question-wheel' : `/sectors/${sectorId}`)}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-3xl font-bold">Generate Product-Specific Questions</h1>
              <p className="text-muted-foreground mt-2">
                {isSiteMode && siteName && `${siteName} - `}
                Select products from {wheelName} to generate specific questions
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Info Alert */}
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          Generate questions about specific products (e.g., "¿Este neumático Michelin tiene buena resistencia?").
          Select {selectedProducts.size} of {products.length} products.
        </AlertDescription>
      </Alert>

      {/* Product Selection */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                Select Products
              </CardTitle>
              <CardDescription>
                Choose products to generate questions for ({selectedProducts.size} selected)
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleSelectAll}
            >
              {selectedProducts.size === filteredProducts.length ? 'Deselect All' : 'Select All'}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Product List */}
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {filteredProducts.length === 0 ? (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  {searchTerm ? 'No products match your search' : 'No products found'}
                </AlertDescription>
              </Alert>
            ) : (
              filteredProducts.map((product) => (
                <div
                  key={product.id}
                  className={`border rounded-lg p-4 cursor-pointer transition-all ${
                    selectedProducts.has(product.id)
                      ? 'border-primary bg-primary/5 dark:bg-primary/10'
                      : 'hover:bg-accent dark:hover:bg-gray-700/50'
                  }`}
                  onClick={() => handleProductToggle(product.id)}
                >
                  <div className="flex items-start gap-3">
                    <Checkbox
                      checked={selectedProducts.has(product.id)}
                      onCheckedChange={() => handleProductToggle(product.id)}
                    />
                    <div className="flex-1">
                      <div className="font-medium text-sm mb-1">
                        {product.product_name}
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        {product.price && (
                          <Badge variant="secondary" className="text-xs">
                            {Number(product.price).toFixed(2)} {product.currency || 'EUR'}
                          </Badge>
                        )}
                        {product.attribute_count && (
                          <Badge variant="outline" className="text-xs">
                            {product.attribute_count} attributes
                          </Badge>
                        )}
                      </div>
                    </div>
                    {selectedProducts.has(product.id) && (
                      <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0" />
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Configuration */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Language Selection */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5" />
              Question Language
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Select value={selectedLanguage} onValueChange={setSelectedLanguage}>
              <SelectTrigger>
                <SelectValue placeholder="Select language" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="es">Spanish (Español)</SelectItem>
                <SelectItem value="en">English</SelectItem>
                <SelectItem value="de">German (Deutsch)</SelectItem>
                <SelectItem value="fr">French (Français)</SelectItem>
                <SelectItem value="it">Italian (Italiano)</SelectItem>
                <SelectItem value="pt">Portuguese (Português)</SelectItem>
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Questions Per Product */}
        <Card>
          <CardHeader>
            <CardTitle>Questions Per Product</CardTitle>
          </CardHeader>
          <CardContent>
            <Input
              type="number"
              min="1"
              max="50"
              value={questionsPerProduct}
              onChange={(e) => setQuestionsPerProduct(parseInt(e.target.value) || 10)}
            />
            <p className="text-xs text-muted-foreground mt-2">
              Total: {selectedProducts.size * questionsPerProduct} questions
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Question Types */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5" />
            Question Types to Generate
          </CardTitle>
          <CardDescription>
            Select which types of product-specific questions to generate ({selectedQuestionTypes.length} selected)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {QUESTION_TYPES.map((qt) => (
              <div
                key={qt.id}
                className="flex items-start space-x-3 p-3 rounded-lg hover:bg-accent dark:hover:bg-gray-700/50 cursor-pointer"
                onClick={() => handleQuestionTypeToggle(qt.id)}
              >
                <Checkbox
                  id={`qt-${qt.id}`}
                  checked={selectedQuestionTypes.includes(qt.id)}
                  onCheckedChange={() => handleQuestionTypeToggle(qt.id)}
                />
                <div className="flex-1">
                  <Label htmlFor={`qt-${qt.id}`} className="font-medium cursor-pointer">
                    {qt.name}
                  </Label>
                  <p className="text-sm text-muted-foreground">{qt.description}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Action Button */}
      <Card>
        <CardHeader>
          <CardTitle>Ready to Generate</CardTitle>
          <CardDescription>
            This will generate {selectedProducts.size * questionsPerProduct} product-specific questions
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {selectedProducts.size > 0 && (
            <Alert>
              <CheckCircle2 className="h-4 w-4" />
              <AlertDescription>
                <strong>{selectedProducts.size}</strong> products selected
                <br />
                Question types: {selectedQuestionTypes.join(', ')}
                <br />
                Language: {selectedLanguage}
              </AlertDescription>
            </Alert>
          )}

          <div className="flex gap-3 justify-end">
            <Button
              variant="outline"
              onClick={() => navigate(isSiteMode ? '/research/question-wheel' : `/sectors/${sectorId}`)}
              disabled={generating}
            >
              Cancel
            </Button>
            <Button
              onClick={handleGenerate}
              disabled={generating || selectedProducts.size === 0 || selectedQuestionTypes.length === 0}
            >
              {generating ? (
                <>
                  <Sparkles className="mr-2 h-4 w-4 animate-spin" />
                  Starting Generation...
                </>
              ) : (
                <>
                  <Play className="mr-2 h-4 w-4" />
                  Generate Questions
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
